import { ReactRenderer } from '@tiptap/react';
import tippy, { Instance as TippyInstance } from 'tippy.js';
import SlashCommandList, { SlashCommandListRef } from './SlashCommandList';
import { slashCommandItems, filterSlashCommandItems, SlashCommandItem } from './slashCommandItems';
import { SuggestionProps, SuggestionKeyDownProps } from '@tiptap/suggestion';

// Cache module-level de itens dinâmicos (textos dinâmicos com placeholder_key).
// Atualizado em runtime por <DynamicSlashItemsBridge /> que é montado uma vez no app.
let dynamicItemsCache: SlashCommandItem[] = [];

export function setDynamicSlashItems(items: SlashCommandItem[]) {
  dynamicItemsCache = items;
}

// Quando definido (ex.: editor do Contrato SaaS), o menu "/" mostra só esta lista.
let overrideItems: SlashCommandItem[] | null = null;

export function setSlashItemsOverride(items: SlashCommandItem[] | null) {
  overrideItems = items;
}

export function getCombinedItems(): SlashCommandItem[] {
  if (overrideItems) return overrideItems;
  // Dedupe by key — dynamic (DB) items take precedence over static defaults.
  const map = new Map<string, SlashCommandItem>();
  for (const item of slashCommandItems) map.set(item.key, item);
  for (const item of dynamicItemsCache) map.set(item.key, item);
  return Array.from(map.values());
}

function filterCombined(query: string): SlashCommandItem[] {
  if (!query) return getCombinedItems();
  const q = query.toLowerCase();
  return getCombinedItems().filter(
    (item) =>
      item.key.toLowerCase().includes(q) ||
      item.label.toLowerCase().includes(q) ||
      item.category.toLowerCase().includes(q) ||
      item.categoryLabel.toLowerCase().includes(q)
  );
}

export const slashCommandSuggestion = {
  items: ({ query }: { query: string }): SlashCommandItem[] => {
    // Sem query: mostrar tudo (agrupado por categoria em acordeão).
    // Com query: limitar resultados para não estourar a UI.
    const results = filterCombined(query);
    return query ? results.slice(0, 50) : results;
  },

  render: () => {
    let component: ReactRenderer<SlashCommandListRef> | null = null;
    let popup: TippyInstance[] | null = null;

    return {
      onStart: (props: SuggestionProps<SlashCommandItem>) => {
        component = new ReactRenderer(SlashCommandList, {
          props: {
            items: props.items,
            command: props.command,
          },
          editor: props.editor,
        });

        if (!props.clientRect) {
          return;
        }

        popup = tippy('body', {
          getReferenceClientRect: props.clientRect as () => DOMRect,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: 'manual',
          placement: 'bottom-start',
          theme: 'light-border',
          arrow: false,
          offset: [0, 5],
        });
      },

      onUpdate: (props: SuggestionProps<SlashCommandItem>) => {
        component?.updateProps({
          items: props.items,
          command: props.command,
        });

        if (!props.clientRect) {
          return;
        }

        popup?.[0]?.setProps({
          getReferenceClientRect: props.clientRect as () => DOMRect,
        });
      },

      onKeyDown: (props: SuggestionKeyDownProps) => {
        if (props.event.key === 'Escape') {
          popup?.[0]?.hide();
          return true;
        }

        return component?.ref?.onKeyDown(props.event) ?? false;
      },

      onExit: () => {
        popup?.[0]?.destroy();
        component?.destroy();
      },
    };
  },
};
